// Min-heap binario tipado. Reemplaza el `push`+`sort` del prototipo en el planificador Dijkstra.
// El uso es un caso extendido de PriorityQueue: cada elemento tiene una prioridad numérica
// (menor = antes). Duplicados permitidos; el consumidor debe descartar entradas obsoletas
// comparando la distancia guardada, exactamente igual que el prototipo hace.
export interface HeapEntry<T> {
  priority: number;
  value: T;
}

export class MinHeap<T> {
  private data: HeapEntry<T>[] = [];

  get size(): number {
    return this.data.length;
  }

  push(priority: number, value: T): void {
    this.data.push({ priority, value });
    this.bubbleUp(this.data.length - 1);
  }

  pop(): HeapEntry<T> | undefined {
    if (this.data.length === 0) return undefined;
    const top = this.data[0]!;
    const last = this.data.pop()!;
    if (this.data.length > 0) {
      this.data[0] = last;
      this.sinkDown(0);
    }
    return top;
  }

  peek(): HeapEntry<T> | undefined {
    return this.data[0];
  }

  private bubbleUp(i: number): void {
    const item = this.data[i]!;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      const p = this.data[parent]!;
      if (p.priority <= item.priority) break;
      this.data[i] = p;
      i = parent;
    }
    this.data[i] = item;
  }

  private sinkDown(i: number): void {
    const n = this.data.length;
    const item = this.data[i]!;
    while (true) {
      const l = 2 * i + 1;
      const r = 2 * i + 2;
      let best = i;
      let bestP = item.priority;
      if (l < n && this.data[l]!.priority < bestP) {
        best = l;
        bestP = this.data[l]!.priority;
      }
      if (r < n && this.data[r]!.priority < bestP) {
        best = r;
      }
      if (best === i) break;
      this.data[i] = this.data[best]!;
      i = best;
    }
    this.data[i] = item;
  }
}
